import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { FilmStrip, Heart, ArrowLeft, User, PaperPlaneTilt, ChatCircleDots } from '@phosphor-icons/react';
import { motion } from 'framer-motion';
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogFooter,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogAction,
  AlertDialogCancel,
} from '../components/ui/alert-dialog';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const CommunityPostDetail = ({ user }) => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [post, setPost] = useState(null);
  const [comments, setComments] = useState([]);
  const [comment, setComment] = useState('');
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(null); // { type: 'post' } | { type: 'comment', id }

  const authHeaders = () => ({
    headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
  });

  const fetchData = useCallback(async () => {
    try {
      const [pRes, cRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/community/posts/${id}`),
        axios.get(`${BACKEND_URL}/api/community/posts/${id}/comments`),
      ]);
      setPost(pRes.data);
      setComments(cRes.data);
    } catch (error) {
      toast.error('Failed to load post');
      navigate('/community');
    } finally {
      setLoading(false);
    }
  }, [id, navigate]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleLike = async () => {
    if (!user) {
      toast.error('Please login to like');
      return;
    }
    try {
      const res = await axios.post(`${BACKEND_URL}/api/community/posts/${id}/like`, {}, authHeaders());
      setPost((prev) => ({
        ...prev,
        likes: res.data.liked
          ? [...(prev.likes || []), user.id]
          : (prev.likes || []).filter((u) => u !== user.id),
      }));
    } catch (error) {
      toast.error('Failed to like');
    }
  };

  const handleComment = async () => {
    if (!user) {
      toast.error('Please login to comment');
      return;
    }
    if (!comment.trim()) return;
    setSubmitting(true);
    try {
      const res = await axios.post(
        `${BACKEND_URL}/api/community/posts/${id}/comments`,
        { content: comment },
        authHeaders()
      );
      setComments((prev) => [...prev, res.data]);
      setComment('');
      setPost((prev) => ({ ...prev, comment_count: (prev.comment_count || 0) + 1 }));
    } catch (error) {
      toast.error('Failed to comment');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteComment = async (commentId) => {
    try {
      await axios.delete(
        `${BACKEND_URL}/api/community/posts/${id}/comments/${commentId}`,
        authHeaders()
      );
      setComments((prev) => prev.filter((c) => c.id !== commentId));
      setPost((prev) => ({ ...prev, comment_count: Math.max(0, (prev.comment_count || 0) - 1) }));
    } catch (error) {
      toast.error('Failed to delete');
    } finally {
      setConfirmDelete(null);
    }
  };

  const handleDeletePost = async () => {
    try {
      await axios.delete(`${BACKEND_URL}/api/community/posts/${id}`, authHeaders());
      toast.success('Post deleted');
      navigate('/community');
    } catch (error) {
      toast.error('Failed to delete');
    } finally {
      setConfirmDelete(null);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-gold" />
      </div>
    );
  }

  if (!post) return null;

  const liked = user && (post.likes || []).includes(user.id);
  const canDeletePost = user && (user.role === 'admin' || user.id === post.author_id);

  return (
    <div className="min-h-screen bg-background">
      <div className="noise-overlay" />
      <nav data-web-chrome="nav" className="relative z-10 glass-card sticky top-0">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex justify-between items-center">
            <button
              onClick={() => navigate('/')}
              data-testid="post-nav-home"
              className="flex items-center space-x-3"
            >
              <img src="/fanzingy-logo.jpeg" alt="FanZingy" className="h-10 w-10 rounded-full object-cover ring-1 ring-white/10" data-testid="brand-logo" />
            <span className="font-heading text-2xl font-bold tracking-tight">Fan<span className="text-gold">Zingy</span></span>
            </button>
            <button
              data-testid="post-back-community"
              onClick={() => navigate('/community')}
              className="btn-secondary flex items-center space-x-2"
            >
              <ArrowLeft size={18} />
              <span>Community</span>
            </button>
          </div>
        </div>
      </nav>

      <div className="relative max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <motion.div
          initial={{ opacity: 0, y: 15 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-card border border-white/5 rounded-lg p-8 mb-8"
          data-testid="post-detail-card"
        >
          <div className="flex items-start justify-between mb-5">
            <div className="flex items-center space-x-3">
              <div className="w-12 h-12 rounded-full bg-gold/10 border border-gold/30 flex items-center justify-center">
                <User size={22} className="text-gold" weight="bold" />
              </div>
              <div>
                <div className="font-body font-bold">{post.author_name}</div>
                <div className="flex items-center gap-2 text-xs text-slate-400">
                  <span className="uppercase tracking-wide">{post.author_role}</span>
                  <span>•</span>
                  <span>{new Date(post.created_at).toLocaleString()}</span>
                </div>
              </div>
            </div>
            <span className="inline-block px-3 py-1 bg-gold/10 border border-gold/30 rounded-sm text-gold text-xs font-bold uppercase">
              {post.industry}
            </span>
          </div>
          <h1 className="font-heading text-3xl font-bold mb-4">{post.title}</h1>
          <p className="font-body text-slate-300 whitespace-pre-wrap leading-relaxed mb-6">
            {post.content}
          </p>
          <div className="flex items-center justify-between pt-4 border-t border-white/5">
            <div className="flex items-center gap-5">
              <button
                data-testid="post-detail-like"
                aria-pressed={liked}
                aria-label={`${liked ? 'Unlike' : 'Like'} post, ${(post.likes || []).length} likes`}
                onClick={handleLike}
                className={`flex items-center gap-2 transition-colors ${
                  liked ? 'text-gold' : 'text-slate-400 hover:text-gold'
                }`}
              >
                <Heart size={22} weight={liked ? 'fill' : 'regular'} />
                <span className="font-mono tabular-nums">{(post.likes || []).length}</span>
              </button>
              <div className="flex items-center gap-2 text-slate-400">
                <ChatCircleDots size={22} />
                <span className="font-mono tabular-nums">{comments.length}</span>
              </div>
            </div>
            {canDeletePost && (
              <button
                data-testid="post-detail-delete"
                onClick={() => setConfirmDelete({ type: 'post' })}
                className="text-xs text-slate-400 hover:text-red-400 uppercase tracking-wide"
              >
                Delete Post
              </button>
            )}
          </div>
        </motion.div>

        {/* Comments */}
        <div>
          <h2 className="font-heading text-2xl font-bold mb-5">
            COMMENTS <span className="text-gold">({comments.length})</span>
          </h2>

          {user ? (
            <div className="bg-card border border-white/5 rounded-lg p-4 mb-6">
              <textarea
                data-testid="post-comment-input"
                rows={3}
                placeholder="Share your thoughts..."
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                className="w-full bg-white/5 border border-white/10 rounded-sm px-4 py-3 text-white font-body focus:outline-none focus:border-gold resize-none mb-3"
              />
              <div className="flex justify-end">
                <button
                  data-testid="post-comment-submit"
                  onClick={handleComment}
                  disabled={submitting || !comment.trim()}
                  className="btn-primary flex items-center gap-2 disabled:opacity-50"
                >
                  <PaperPlaneTilt size={18} weight="bold" />
                  <span>{submitting ? 'Sending...' : 'Comment'}</span>
                </button>
              </div>
            </div>
          ) : (
            <div className="bg-card border border-white/5 rounded-lg p-6 text-center mb-6">
              <p className="text-slate-400 mb-3">Login to join the conversation</p>
              <button
                data-testid="post-login-cta"
                onClick={() => navigate('/login')}
                className="btn-primary"
              >
                Login
              </button>
            </div>
          )}

          <div data-testid="post-comments-list" className="space-y-3">
            {comments.length === 0 ? (
              <div className="text-center py-8 text-slate-400 font-body">
                No comments yet. Be the first to share.
              </div>
            ) : (
              comments.map((c) => {
                const canDelete = user && (user.role === 'admin' || user.id === c.author_id);
                return (
                  <motion.div
                    key={c.id}
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="bg-card border border-white/5 rounded-lg p-4"
                    data-testid={`post-comment-${c.id}`}
                  >
                    <div className="flex items-start justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <div className="w-8 h-8 rounded-full bg-gold/10 border border-gold/30 flex items-center justify-center">
                          <User size={16} className="text-gold" weight="bold" />
                        </div>
                        <div>
                          <div className="font-body text-sm font-bold">{c.author_name}</div>
                          <div className="text-xs text-slate-400">
                            <span className="uppercase tracking-wide">{c.author_role}</span>
                            <span> • {new Date(c.created_at).toLocaleString()}</span>
                          </div>
                        </div>
                      </div>
                      {canDelete && (
                        <button
                          data-testid={`post-comment-delete-${c.id}`}
                          onClick={() => setConfirmDelete({ type: 'comment', id: c.id })}
                          className="text-xs text-slate-400 hover:text-red-400 uppercase tracking-wide"
                        >
                          Delete
                        </button>
                      )}
                    </div>
                    <p className="font-body text-slate-300 whitespace-pre-wrap pl-10">
                      {c.content}
                    </p>
                  </motion.div>
                );
              })
            )}
          </div>
        </div>
      </div>

      <AlertDialog open={!!confirmDelete} onOpenChange={(open) => !open && setConfirmDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {confirmDelete?.type === 'post' ? 'Delete this post?' : 'Delete this comment?'}
            </AlertDialogTitle>
            <AlertDialogDescription>This can't be undone.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() =>
                confirmDelete?.type === 'post'
                  ? handleDeletePost()
                  : handleDeleteComment(confirmDelete.id)
              }
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default CommunityPostDetail;
